import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowRight, CheckCircle2, GraduationCap, LinkIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { z } from 'zod'
import { api } from '@/api/client'
import { toApiError } from '@/api/errors'
import { EmptyState, LoadingState } from '@/components/feedback/States'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { preferences } from '@/features/preferences/store'
import { LanguagePicker } from '@/features/preferences/components/PreferenceControls'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { isLanguage } from '@/i18n'
import { APP_NAME } from '@/utils/brand'

interface InvitationInfo {
  name: string
  email: string
  role: string
  portal: 'admin' | 'staff' | 'family'
  locale: string
  school: { name: string; logoUrl: string | null }
  invitedBy: string
  expiresAt: string
}

const schema = z
  .object({
    password: z.string().min(8, 'Use at least 8 characters.').max(128),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Passwords do not match.' })
type Form = z.infer<typeof schema>

/** Public page behind an invitation link: the invited user chooses their password. */
export function AcceptInvitationPage() {
  const { t } = useTranslation('auth')
  useDocumentTitle('Choose your password')
  const token = useParams().token ?? ''
  const info = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<InvitationInfo>(`/public/invitations/${token}`),
    retry: false,
  })
  const accept = useMutation({
    mutationFn: (password: string) =>
      api.post<{ email: string; portal: string }>(`/public/invitations/${token}/accept`, { password }),
  })
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(schema), defaultValues: { password: '', confirm: '' } })

  // Show the page in the language the account was created with.
  const locale = info.data?.locale
  useEffect(() => {
    if (isLanguage(locale)) preferences.setLocale(locale)
  }, [locale])

  const onSubmit = handleSubmit(async (v) => {
    try {
      await accept.mutateAsync(v.password)
    } catch (e) {
      applyServerErrors(e, setError, ['password'])
    }
  })

  const body = info.isPending ? (
    <LoadingState />
  ) : info.error ? (
    <Card>
      <EmptyState
        icon={<LinkIcon />}
        title={t('This link no longer works')}
        description={toApiError(info.error).message}
        action={
          <Button asChild variant="secondary">
            <Link to="/login">{t('Go to sign-in')}</Link>
          </Button>
        }
      />
    </Card>
  ) : accept.isSuccess ? (
    <Card className="p-6 text-center">
      <CheckCircle2 className="mx-auto size-10 text-success-600" aria-hidden="true" />
      <h1 className="mt-3 text-lg font-semibold text-ink">{t('Your password is set')}</h1>
      <p className="mt-1 text-sm text-muted">
        {t('Sign in with {{email}} to get started.', { email: accept.data.email })}
      </p>
      <Button asChild className="mt-5" rightIcon={<ArrowRight />}>
        <Link to={`/login?portal=${accept.data.portal}`}>{t('Sign in')}</Link>
      </Button>
    </Card>
  ) : (
    <Card className="p-6">
      <div className="mb-5 flex items-center gap-3">
        <Avatar name={info.data.school.name} src={info.data.school.logoUrl} />
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink">{info.data.school.name}</p>
          <p className="text-xs text-muted">{t('Invitation from {{name}}', { name: info.data.invitedBy })}</p>
        </div>
      </div>
      <h1 className="text-lg font-semibold text-ink">{t('Welcome, {{name}}', { name: info.data.name })}</h1>
      <p className="mt-1 mb-5 text-sm text-muted">
        {t('Choose a password for {{email}}. You will use it to sign in.', { email: info.data.email })}
      </p>
      <form onSubmit={(e) => void onSubmit(e)} noValidate className="space-y-4">
        {accept.error && !errors.password ? (
          <Alert tone="danger" title={t('The password was not saved')}>
            {toApiError(accept.error).message}
          </Alert>
        ) : null}
        <input type="email" autoComplete="username" value={info.data.email} readOnly hidden />
        <FormField
          label={t('New password')}
          error={errors.password?.message}
          hint={t('At least 8 characters.')}
          required
        >
          <Input type="password" autoComplete="new-password" autoFocus {...register('password')} />
        </FormField>
        <FormField label={t('Confirm the password')} error={errors.confirm?.message} required>
          <Input type="password" autoComplete="new-password" {...register('confirm')} />
        </FormField>
        <Button type="submit" className="w-full" loading={accept.isPending}>
          {t('Save and continue')}
        </Button>
      </form>
    </Card>
  )

  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-canvas px-4 py-10">
      <div className="absolute top-4 right-4">
        <LanguagePicker compact />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2 text-ink">
          <span className="bg-brand-gradient grid size-9 place-items-center rounded-xl text-white">
            <GraduationCap className="size-5" aria-hidden="true" />
          </span>
          <span className="font-semibold">{APP_NAME}</span>
        </div>
        {body}
      </div>
    </div>
  )
}
